import { useEffect, useMemo, useState } from 'react'
import { useClient, useQuery } from '@based/sdk/react'
import { styled } from 'inlines'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import { InitiativeCard } from '@app/components/blocks/card/InitiativeCard'
import { SectionHeader } from '@app/components/blocks/sectionHeader/SectionHeader'
import { getSpotlightTileLayouts } from '@app/components/blocks/spotlightInitiativeGrid/getSpotlightTileLayouts'
import Column from '@app/components/layout/Column'
import { labelAddressBuilder } from '@app/util/helpers'
import useMediaQuery from '@app/util/useMediaQuery'
import { pages } from '@app/util/links'
import { useTranslations } from '@i18n/useLanguage'
import type { PlotSpotlightModel } from 'functions/app/spotlight/list/listTypes'

const spotlightGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(250px, 1fr))',
  '@media (max-width: 1200px)': {
    gridTemplateColumns: 'repeat(2, minmax(250px, 1fr))',
  },
  '@media (max-width: 800px)': {
    gridTemplateColumns: 'repeat(1, minmax(250px, 1fr))',
  },
  columnGap: '1.5rem',
  rowGap: '2rem',
} as const

export const SpotlightedPlotsSection = () => {
  const t = useTranslations()
  const translations = t.app.discoveryPage.tabs.plots
  const commonTranslations = t.app.common
  const isMobile = useMediaQuery()
  const client = useClient()
  const [mounted, setMounted] = useState(false)
  const [spotlights, setSpotlights] = useState<PlotSpotlightModel[]>([])

  const dataServerQuery = useQuery('list-spotlight', {
    entityType: 'plot',
  })
  const serverSpotlights: PlotSpotlightModel[] = dataServerQuery.data ?? []

  useEffect(() => {
    const close = client
      .query('list-spotlight', { entityType: 'plot' })
      .subscribe((result: PlotSpotlightModel[]) => {
        setSpotlights(result)
        setMounted(true)
      })
    return () => close()
  }, [client])

  const renderedSpotlights = mounted ? spotlights : serverSpotlights
  const { plots, isPrimaryByIndex } = useMemo(() => {
    const withPlot = renderedSpotlights.filter(
      (
        s,
      ): s is PlotSpotlightModel & {
        plot: NonNullable<PlotSpotlightModel['plot']>
      } => Boolean(s.plot),
    )
    return {
      plots: withPlot.map((s) => s.plot),
      isPrimaryByIndex: withPlot.map((s) => Boolean(s.isPrimary)),
    }
  }, [renderedSpotlights])
  const layouts = useMemo(
    () =>
      getSpotlightTileLayouts(plots.length, isMobile, isPrimaryByIndex),
    [plots.length, isMobile, isPrimaryByIndex],
  )

  if (plots.length === 0) {
    return null
  }

  return (
    <Column alignItems="stretch">
      <SectionHeader
        title={translations.spotlight.title}
        subtitle={translations.spotlight.subTitle}
      />
      <styled.div style={spotlightGridStyle}>
        {plots.map((plot, index) => {
          const { isSpotlight, gridColumn } = layouts[index] ?? {
            isSpotlight: false,
            gridColumn: null,
          }
          return (
            <InitiativeCard
              key={plot.slug}
              variant={isSpotlight ? 'spotlight' : 'default'}
              style={{ gridColumn }}
              to={pages.plot(plot.slug)}
              image={plot.media?.[0] ?? defaultProjectPicture}
              title={plot.name}
              description={isSpotlight ? plot.description : null}
              location={labelAddressBuilder(
                plot.location.address,
                commonTranslations.notSpecified,
              )}
              tagText={
                commonTranslations.plotApplicationStatus[plot.applicationStatus]
              }
              tagVariant={
                plot.applicationStatus === 'applicationOpen'
                  ? 'success'
                  : 'neutral'
              }
            />
          )
        })}
      </styled.div>
    </Column>
  )
}
